import * as Radix from "@radix-ui/themes";
import classNames from "classnames";
import { Dialog as RadixDialog } from "radix-ui";
import * as React from "react";
import styles from "./Dialog.module.css";
import { DialogContext } from "~/contexts/dialogContext";

export type DialogWidth =
  | "narrow"
  | "default"
  | "wide"
  | "max"
  | "vendr-link"
  | "screen";
export type DialogHeight = "default" | "auto" | "tall" | "max" | "screen";

export const Dialog = React.memo(function Dialog({
  children,
  isOpen,
  isPadded,
  onOpenChange,
  a11yTitle,
  a11yDescription,
  closeOnInteractionOutside,
  hideCloseButton,
  className,
  width = "default",
  height = "default",
}: {
  isPadded?: boolean;
  children?: React.ReactNode;
  closeOnInteractionOutside?: boolean;
  hideCloseButton?: boolean;
  isOpen: boolean;
  onOpenChange: ({ isOpen }: { isOpen: boolean }) => void;
  a11yTitle: string;
  a11yDescription: string;
  className?: string;
  width?: DialogWidth;
  height?: DialogHeight;
}) {
  const { container, content } = React.useContext(DialogContext);
  const paddedClass = isPadded ? styles.padded : "";

  return (
    <RadixDialog.Root
      open={isOpen}
      onOpenChange={(open) => onOpenChange({ isOpen: open })}
    >
      <RadixDialog.Portal container={container.current}>
        <RadixDialog.Overlay className={styles.dialogOverlay} />
        <Radix.Box position="fixed" top="50%" left="50%" width="100%">
          <RadixDialog.Content
            className={classNames(
              styles.dialogContent,
              paddedClass,
              styles[`width-${width}`],
              styles[`height-${height}`],
              className,
            )}
            ref={content}
            onInteractOutside={(e) =>
              closeOnInteractionOutside ? e : e.preventDefault()
            }
          >
            <Radix.VisuallyHidden asChild>
              <RadixDialog.Title>{a11yTitle}</RadixDialog.Title>
            </Radix.VisuallyHidden>
            <Radix.VisuallyHidden asChild>
              <RadixDialog.Description>
                {a11yDescription}
              </RadixDialog.Description>
            </Radix.VisuallyHidden>

            {children}
          </RadixDialog.Content>
        </Radix.Box>

        {!hideCloseButton && <RadixDialog.Close />}
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
});
